// ~/apps/themusicchurch-app/frontend/src/pages/Contact.jsx

import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { FiMail, FiPhone, FiMapPin, FiSend } from "react-icons/fi";
import Button from "../components/Common/Button";

const ContactWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.primary} 0%,
    ${(props) => props.theme.colors.secondary} 100%
  );
  color: white;
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing["2xl"]};
`;

const InfoCard = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.lg};
  padding: ${(props) => props.theme.spacing.xl};
  background: ${(props) => props.theme.colors.gray[50]};
  border-radius: 12px;
  align-items: center;
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.theme.fontSizes.xl};
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  h4 {
    color: ${(props) => props.theme.colors.primary};
    margin-bottom: ${(props) => props.theme.spacing.xs};
  }

  p {
    color: ${(props) => props.theme.colors.gray[600]};
    margin: 0;
  }
`;

const ContactForm = styled(motion.form)`
  background: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  box-shadow: ${(props) => props.theme.shadows.lg};
`;

const FormTitle = styled.h3`
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: ${(props) => props.theme.spacing["2xl"]};
`;

const FormGroup = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  color: ${(props) => props.theme.colors.gray[700]};
  font-weight: 500;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.gray[300]};
  border-radius: 8px;
  font-size: ${(props) => props.theme.fontSizes.base};
  transition: all ${(props) => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.gray[400]};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.gray[300]};
  border-radius: 8px;
  font-size: ${(props) => props.theme.fontSizes.base};
  font-family: inherit;
  resize: vertical;
  min-height: 150px;
  transition: all ${(props) => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.gray[400]};
  }
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: ${(props) => props.theme.fontSizes.sm};
  margin-top: ${(props) => props.theme.spacing.xs};
  display: block;
`;

const SuccessMessage = styled(motion.div)`
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  padding: ${(props) => props.theme.spacing.lg};
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
`;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      console.log("Form data:", data);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      reset();

      // Hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    }, 1000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - The Music Church</title>
        <meta
          name="description"
          content="Get in touch with The Music Church. We'd love to hear from you!"
        />
      </Helmet>

      <ContactWrapper>
        <HeroSection>
          <PageTitle>Contact Us</PageTitle>
          <p>We'd love to hear from you</p>
        </HeroSection>

        <ContentSection>
          <ContactGrid>
            <ContactInfo
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <h2 style={{ color: "#2C3E50", marginBottom: "1rem" }}>
                  Get in Touch
                </h2>
                <p style={{ color: "#6C757D", lineHeight: "1.8" }}>
                  Whether you have questions about our ministries, want to get
                  involved, or simply want to connect, we're here to help. Reach
                  out to us through any of the methods below or fill out the
                  contact form.
                </p>
              </motion.div>

              <InfoCard as={motion.div} variants={itemVariants}>
                <IconWrapper>
                  <FiMail />
                </IconWrapper>
                <InfoContent>
                  <h4>Email</h4>
                  <p>themusicchurch@gmail.com</p>
                </InfoContent>
              </InfoCard>

              <InfoCard as={motion.div} variants={itemVariants}>
                <IconWrapper>
                  <FiPhone />
                </IconWrapper>
                <InfoContent>
                  <h4>Phone</h4>
                  <p>(555) 123-4567</p>
                </InfoContent>
              </InfoCard>

              <InfoCard as={motion.div} variants={itemVariants}>
                <IconWrapper>
                  <FiMapPin />
                </IconWrapper>
                <InfoContent>
                  <h4>Address</h4>
                  <p>
                    123 Faith Street
                    <br />
                    Worship City, WC 12345
                  </p>
                </InfoContent>
              </InfoCard>
            </ContactInfo>

            <ContactForm
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormTitle>Send us a Message</FormTitle>

              {submitSuccess && (
                <SuccessMessage
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  Thank you for your message! We'll get back to you soon.
                </SuccessMessage>
              )}

              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your Name"
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && (
                  <ErrorMessage>{errors.name.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email && (
                  <ErrorMessage>{errors.email.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  type="text"
                  placeholder="What's this about?"
                  {...register("subject")}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Your message..."
                  {...register("message", {
                    required: "Message is required",
                    minLength: {
                      value: 10,
                      message: "Message must be at least 10 characters",
                    },
                  })}
                />
                {errors.message && (
                  <ErrorMessage>{errors.message.message}</ErrorMessage>
                )}
              </FormGroup>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={isSubmitting}
                icon={<FiSend />}
                iconPosition="right"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </ContactForm>
          </ContactGrid>
        </ContentSection>
      </ContactWrapper>
    </>
  );
};

export default Contact;
